import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import PropertyCard from "@/components/PropertyCard";
import ContactForm from "@/components/ContactForm";
import { Reveal, Eyebrow, RevealLines } from "@/components/Reveal";
import Seo from "@/components/Seo";

const FASCE = {
  "Qualsiasi": () => true,
  "Fino a 600.000 €": (v) => v <= 600000,
  "600.000 – 1.200.000 €": (v) => v > 600000 && v <= 1200000,
  "Oltre 1.200.000 €": (v) => v > 1200000,
};

export default function Vendita() {
  const [properties, setProperties] = useState(null);
  const [tipologia, setTipologia] = useState("Tutte");
  const [zona, setZona] = useState("Tutte");
  const [fascia, setFascia] = useState("Qualsiasi");

  useEffect(() => {
    api.get("/properties", { params: { status: "vendita" } })
      .then((r) => setProperties(r.data))
      .catch(() => setProperties([]));
  }, []);

  const tipologie = useMemo(
    () => ["Tutte", ...new Set((properties || []).map((p) => p.typology))],
    [properties]
  );
  const zone = useMemo(
    () => ["Tutte", ...new Set((properties || []).map((p) => p.zone))],
    [properties]
  );

  const filtered = (properties || []).filter(
    (p) =>
      (tipologia === "Tutte" || p.typology === tipologia) &&
      (zona === "Tutte" || p.zone === zona) &&
      FASCE[fascia](p.price)
  );

  return (
    <div data-testid="vendita-page" className="pt-28 lg:pt-32">
      <Seo
        title="Immobili in vendita a Milano e in Lombardia | IDEAL"
        description="Appartamenti, attici e ville in vendita a Milano e in Lombardia: foto, prezzi, metrature, schede complete e valutazione gratuita per i proprietari."
        path="/vendita"
      />
      <header className="mx-auto max-w-[1440px] px-6 pb-16 lg:px-12">
        <Reveal><Eyebrow>Immobili in vendita</Eyebrow></Reveal>
        <h1>
          <RevealLines
            lines={["Immobili in vendita", "a Milano e in Lombardia."]}
            className="mt-5 font-serif text-4xl font-medium leading-[1.05] lg:text-6xl"
          />
        </h1>
        <Reveal delay={0.2}>
          <p className="mt-6 max-w-xl text-base font-light leading-relaxed text-mut lg:text-lg">
            Una selezione curata di appartamenti, attici e ville: ogni immobile è seguito
            personalmente dal nostro team, dalla valutazione alla firma.
          </p>
        </Reveal>
      </header>

      {/* Filtri */}
      <div className="sticky top-20 z-30 border-y border-ink/10 bg-sand/90 backdrop-blur-xl">
        <div className="mx-auto flex max-w-[1440px] flex-wrap items-end gap-x-10 gap-y-4 px-6 py-5 lg:px-12">
          <div>
            <label className="field-label mb-1" htmlFor="f-tipologia">Tipologia</label>
            <select
              id="f-tipologia"
              data-testid="property-filter-category"
              className="cursor-pointer bg-transparent font-mono text-[13px] text-ink outline-none"
              value={tipologia}
              onChange={(e) => setTipologia(e.target.value)}
            >
              {tipologie.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>
          <div>
            <label className="field-label mb-1" htmlFor="f-zona">Località</label>
            <select
              id="f-zona"
              className="cursor-pointer bg-transparent font-mono text-[13px] text-ink outline-none"
              value={zona}
              onChange={(e) => setZona(e.target.value)}
            >
              {zone.map((z) => <option key={z} value={z}>{z}</option>)}
            </select>
          </div>
          <div>
            <label className="field-label mb-1" htmlFor="f-fascia">Fascia di prezzo</label>
            <select
              id="f-fascia"
              className="cursor-pointer bg-transparent font-mono text-[13px] text-ink outline-none"
              value={fascia}
              onChange={(e) => setFascia(e.target.value)}
            >
              {Object.keys(FASCE).map((f) => <option key={f} value={f}>{f}</option>)}
            </select>
          </div>
          <p className="ml-auto font-mono text-[12px] text-mut" data-testid="vendita-count">
            {properties === null ? "…" : `${filtered.length} immobili`}
          </p>
        </div>
      </div>

      {/* Griglia */}
      <section className="mx-auto max-w-[1440px] px-6 py-20 lg:px-12 lg:py-28">
        {properties === null ? (
          <div className="grid gap-x-8 gap-y-16 sm:grid-cols-2 lg:grid-cols-3">
            {[0, 1, 2, 3, 4, 5].map((i) => (
              <div key={i} className="aspect-[4/3] animate-pulse bg-sand3" />
            ))}
          </div>
        ) : filtered.length > 0 ? (
          <div className="grid gap-x-8 gap-y-16 sm:grid-cols-2 lg:grid-cols-3">
            {filtered.map((p, i) => (
              <Reveal key={p.code} delay={(i % 3) * 0.08}>
                <PropertyCard property={p} />
              </Reveal>
            ))}
          </div>
        ) : (
          <div className="py-16 text-center">
            <p className="font-serif text-3xl italic text-mut">Nessun immobile corrisponde ai filtri selezionati.</p>
            <button
              className="mt-6 text-[12px] uppercase tracking-wide2 text-champagne underline underline-offset-4"
              onClick={() => { setTipologia("Tutte"); setZona("Tutte"); setFascia("Qualsiasi"); }}
              data-testid="vendita-reset-filters"
            >
              Azzera i filtri
            </button>
          </div>
        )}
      </section>

      {/* Proprietari */}
      <section className="bg-ink text-sand" data-testid="vendita-owners">
        <div className="mx-auto grid max-w-[1440px] gap-16 px-6 py-24 lg:grid-cols-2 lg:px-12 lg:py-32">
          <div>
            <Reveal><Eyebrow light>Per i proprietari</Eyebrow></Reveal>
            <RevealLines
              lines={["Vendere casa", "con IDEAL."]}
              className="mt-5 font-serif text-4xl font-medium leading-[1.05] lg:text-5xl"
            />
            <Reveal delay={0.15}>
              <p className="mt-8 max-w-md text-base font-light leading-relaxed text-sand/65">
                Ti aiutiamo a vendere al valore giusto, nei tempi giusti: dalla valutazione
                iniziale alla trattativa, ogni fase è seguita da un consulente dedicato.
              </p>
            </Reveal>
            <div className="mt-10 space-y-5">
              {[
                "Valutazione immobiliare gratuita e senza impegno",
                "Piano di marketing dedicato al tuo immobile",
                "Gestione completa di visite e trattativa",
              ].map((item, i) => (
                <Reveal key={item} delay={0.2 + i * 0.08}>
                  <p className="flex items-start gap-4 text-[15px] font-light text-sand/85">
                    <span className="mt-2 h-1.5 w-1.5 shrink-0 rotate-45 bg-champagne" />
                    {item}
                  </p>
                </Reveal>
              ))}
            </div>
          </div>
          <Reveal delay={0.2}>
            <div className="bg-sand p-8 text-ink lg:p-12">
              <h3 className="font-serif text-3xl">Richiedi una valutazione</h3>
              <p className="mt-2 text-sm font-light text-mut">Gratuita, riservata e senza impegno.</p>
              <div className="mt-8">
                <ContactForm variant="valutazione" testId="valuation-submit-button" />
              </div>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
